/**
 * Parser for tangle operations - extracts code blocks marked for tangling
 */

import type { TangleBlock } from './types.js';

/**
 * Parser state machine states
 */
type ParserState = 'idle' | 'expecting_fence' | 'in_block';

/**
 * Regex patterns used in parsing
 */

// Custom format: <!-- tangle: script.sh -->
const CUSTOM_TANGLE_REGEX = /^<!--\s*tangle:\s*/;

// Quarto format: ```{bash, tangle="script.sh"}
const QUARTO_TANGLE_REGEX = /^```.*tangle="([^"]+)"/;

// Code fence start/end: ``` (with optional language specifier for start)
const FENCE_START_REGEX = /^\s*```/;
const FENCE_END_REGEX = /^\s*```\s*$/;

/**
 * Parse custom tangle comment to extract target filename
 *
 * Input: <!-- tangle: script.sh -->
 * Output: "script.sh"
 *
 * @param line - The line containing the tangle comment
 * @returns The target filename, or null if parsing fails
 */
function parseCustomTangleTargets(line: string): string[] | null {
  // Find "tangle:" position
  const tanglePos = line.indexOf('tangle:');
  if (tanglePos === -1) {
    return null;
  }

  // Extract everything after "tangle:"
  const start = tanglePos + 'tangle:'.length;
  let rest = line.slice(start);

  // Find "-->"
  const endPos = rest.indexOf('-->');
  if (endPos === -1) {
    return null;
  }

  // Extract the target and trim whitespace
  const targets = rest
    .slice(0, endPos)
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  if (targets.length === 0) {
    return null;
  }

  // Preserve order while removing duplicates.
  return [...new Set(targets)];
}

/**
 * Parse Quarto format tangle to extract target filename
 *
 * Input: ```{bash, tangle="script.sh"}
 * Output: "script.sh"
 *
 * @param line - The line containing the Quarto-style code fence
 * @returns The target filename, or null if parsing fails
 */
function parseQuartoTangleTarget(line: string): string | null {
  const match = line.match(QUARTO_TANGLE_REGEX);
  return match ? match[1] : null;
}

/**
 * Extract all tangle blocks from markdown content
 *
 * Supports two formats:
 *
 * 1. Custom format:
 *    <!-- tangle: script.sh -->
 *    ```bash
 *    code here
 *    ```
 *
 * 2. Quarto format:
 *    ```{bash, tangle="script.sh"}
 *    code here
 *    ```
 *
 * @param content - The markdown content to parse
 * @returns Array of TangleBlock objects
 */
export function extractTangleBlocks(content: string): TangleBlock[] {
  const lines = content.split('\n');
  const blocks: TangleBlock[] = [];

  let state: ParserState = 'idle';
  let currentTargets: string[] = [];
  let currentFormat: 'custom' | 'quarto' = 'custom';
  let currentContent: string[] = [];
  let blockIndex = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    switch (state) {
      case 'idle':
        // Check for custom format tangle comment
        if (CUSTOM_TANGLE_REGEX.test(line)) {
          const targets = parseCustomTangleTargets(line);
          if (targets) {
            currentTargets = targets;
            currentFormat = 'custom';
            currentContent = [];
            blockIndex++;
            state = 'expecting_fence';
          }
        }
        // Check for Quarto format
        else if (QUARTO_TANGLE_REGEX.test(line)) {
          const target = parseQuartoTangleTarget(line);
          if (target) {
            currentTargets = [target];
            currentFormat = 'quarto';
            currentContent = [];
            blockIndex++;
            state = 'in_block';
          }
        }
        break;

      case 'expecting_fence':
        // Looking for code fence after custom tangle comment
        if (FENCE_START_REGEX.test(line)) {
          state = 'in_block';
        }
        // Skip other lines (headings, blank lines, text, etc.)
        break;

      case 'in_block':
        // Check for code fence end
        if (FENCE_END_REGEX.test(line)) {
          const blockContent = currentContent.join('\n');
          for (const target of currentTargets) {
            blocks.push({
              target,
              content: blockContent,
              blockIndex,
              format: currentFormat,
            });
          }

          // Reset state
          currentTargets = [];
          currentContent = [];
          state = 'idle';
        } else {
          // Record content exactly as-is
          currentContent.push(line);
        }
        break;
    }
  }

  return blocks;
}

/**
 * Group tangle blocks by target filename
 *
 * Preserves block order by using the blockIndex.
 *
 * @param blocks - Array of TangleBlock objects
 * @returns Map of target filename to array of blocks (sorted by blockIndex)
 */
export function groupBlocksByTarget(blocks: TangleBlock[]): Map<string, TangleBlock[]> {
  const grouped = new Map<string, TangleBlock[]>();

  for (const block of blocks) {
    const existing = grouped.get(block.target);
    if (existing) {
      existing.push(block);
    } else {
      grouped.set(block.target, [block]);
    }
  }

  // Sort each group by blockIndex to preserve order
  for (const [, blockList] of grouped) {
    blockList.sort((a, b) => a.blockIndex - b.blockIndex);
  }

  return grouped;
}
